import React from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { useTheme } from './hooks/useTheme';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import Inventaire from './pages/Inventaire';
import Mouvements from './pages/Mouvements';
import Categories from './pages/Categories';
import Utilisateurs from './pages/Utilisateurs';
import Organisations from './pages/Organisations';
import Rapport from './pages/Rapport';
import './App.css';

const NAV = [
  { to: '/', label: 'Tableau de bord', icon: '📊', roles: ['super_admin','admin','technicien','viewer'] },
  { to: '/inventaire', label: 'Inventaire', icon: '📦', roles: ['super_admin','admin','technicien','viewer'] },
  { to: '/mouvements', label: 'Mouvements', icon: '🔄', roles: ['super_admin','admin','technicien','viewer'] },
  { to: '/categories', label: 'Catégories', icon: '🗂', roles: ['super_admin','admin','technicien'] },
  { to: '/rapports', label: 'Rapports', icon: '📋', roles: ['super_admin','admin'] },
  { to: '/organisations', label: 'Organisations', icon: '🏢', roles: ['super_admin','admin'] },
  { to: '/utilisateurs', label: 'Utilisateurs', icon: '👥', roles: ['super_admin','admin'] },
];

function AppShell() {
  const { user, logout, loading } = useAuth();
  const { theme, toggleTheme } = useTheme();

  if (loading) return (
    <div className="loading" style={{ minHeight: '100vh' }}>
      <div className="spinner"></div>Chargement...
    </div>
  );

  if (!user) return <AuthPage />;

  const visibleNav = NAV.filter(n => n.roles.includes(user.role));

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-logo">
          <div className="logo-icon">⬡</div>
          <div>
            <div className="logo-title">NetStock</div>
            <div className="logo-sub" style={{ fontSize: 10, color: '#6b7280' }}>
              {user.organisations?.nom || user.role === 'super_admin' ? 'Super Admin' : ''}
            </div>
          </div>
        </div>

        {/* Badge organisation */}
        {user.organisations?.nom && (
          <div style={{
            margin: '0 12px 8px', padding: '6px 10px',
            background: 'rgba(232,25,44,0.1)', borderRadius: 6,
            fontSize: 11, color: '#e8192c', fontWeight: 600,
            border: '1px solid rgba(232,25,44,0.2)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            🏢 {user.organisations.nom}
          </div>
        )}

        <nav className="sidebar-nav">
          {visibleNav.map(n => (
            <NavLink key={n.to} to={n.to} end={n.to === '/'} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <span className="nav-icon">{n.icon}</span>
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-user">
          <button className="theme-toggle" onClick={toggleTheme}>
            {theme === 'light' ? '🌙 Mode nuit' : '☀️ Mode jour'}
          </button>
          <div className="sidebar-user-card">
            <div className="user-avatar">{user.nom.charAt(0).toUpperCase()}</div>
            <div style={{ overflow: 'hidden', flex: 1 }}>
              <div className="user-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.nom}</div>
              <div className="user-role">{user.role.replace('_', ' ')}</div>
            </div>
          </div>
          <button className="btn btn-sm" onClick={logout} style={{
            width: '100%', justifyContent: 'center',
            background: 'rgba(255,255,255,0.06)', color: '#8b92a5',
            border: '1px solid rgba(255,255,255,0.08)', fontSize: 12
          }}>
            ⏻ Déconnexion
          </button>
        </div>

        <div className="sidebar-footer">
          <div className="status-dot"></div>
          <span>Système opérationnel</span>
        </div>
      </aside>

      <main className="main-content">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/inventaire" element={<Inventaire />} />
          <Route path="/mouvements" element={<Mouvements />} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/rapports" element={<Rapport />} />
          <Route path="/organisations" element={<Organisations />} />
          <Route path="/utilisateurs" element={<Utilisateurs />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" toastOptions={{
          style: { background: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border)', fontFamily: 'Inter', fontSize: 13 },
          success: { iconTheme: { primary: '#10b981', secondary: '#fff' } },
          error: { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
        }} />
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}
